import React, { useMemo } from 'react';
import ConditionalRender from "../../../ConditionalRender";
import { Icons } from "../Icons";

/**
 * Size → CSS class mapping.
 * Each size controls padding, font-size, and min-height via SCSS.
 */
const BUTTON_SIZES = {
    Small: "small-button",
    Medium: "medium-button",
    Large: "large-button",
};

const BUTTON_ICON_PX = {
    Small: 20,
    Medium: 24,
    Large: 24,
};

/**
 * Type → CSS class mapping.
 * Controls background color, text color, borders, and hover/active states.
 * Ghost is like Tertiary but with no background at all.
 */
const BUTTON_TYPES = {
    Primary: "primary-button",
    Secondary: "secondary-button",
    Tertiary: "tertiary-button",
    Ghost: "ghost-button",
    Danger: "danger-button",
};

const LOADER_VARIANT = {
    Primary: "primary",
    Danger: "primary",
    Secondary: "secondary",
    Tertiary: "secondary",
    Ghost: "secondary",
};

/**
 * Button — The main button component.
 *
 * Supports 4 visual types (Primary, Secondary, Tertiary, Ghost),
 * 3 sizes (Small, Medium, Large), optional leading/trailing icons,
 * a loading state with animated dots, and inline error display.
 *
 * @param {string}  Size              - 'Small' | 'Medium' | 'Large'
 * @param {string}  Type              - 'Primary' | 'Secondary' | 'Tertiary' | 'Ghost' | 'Danger'
 * @param {boolean} ShowLeadingIcon   - Show an icon before the label
 * @param {boolean} ShowTrailingIcon  - Show an icon after the label
 * @param {string}  LeadingIcon       - Icon name for leading position
 * @param {string}  TrailingIcon      - Icon name for trailing position
 * @param {func}    onClick           - Click handler (ignored when disabled or loading)
 * @param {boolean} disabled          - Disabled state
 * @param {boolean} isLoading         - Replaces label with a loading animation; disables the button and signals aria-busy
 * @param {string}  error             - Error message shown below the button
 */
export const Button = ({
    children,
    Size = "Small",
    Type = "Primary",
    ShowLeadingIcon = false,
    ShowTrailingIcon = false,
    LeadingIcon = null,
    TrailingIcon = null,
    LeadingIconProps = {},
    TrailingIconProps = {},
    onClick,
    type = "button",
    disabled = false,
    isLoading = false,
    fontClass = "body-s-bold",
    customClassName = "",
    customIconClassName = "",
    buttonStyle = { width: "max-content" },
    innerButtonStyle = { width: "100%" },
    error = "",
}) => {
    // Memoize class string — only recomputes when Type, Size, fontClass, or customClassName change
    const computed_classes = useMemo(
        () => [BUTTON_TYPES[Type], BUTTON_SIZES[Size], fontClass, customClassName].filter(Boolean).join(" "),
        [Type, Size, fontClass, customClassName]
    );

    /** Renders the button inner content: loading animation OR label with optional icons */
    const renderButtonContent = () => {
        if (isLoading) {
            // Loader color depends on button type — primary gets white dots, others get themed dots
            let loading_color_class = `loader-${LOADER_VARIANT[Type] || "secondary"}-${Size}`;
            return (
                <>
                    {/* Hidden icons + text keep the button width stable during loading */}
                    {ShowLeadingIcon && LeadingIcon && <Icons name={LeadingIcon} size={LeadingIconProps.size || BUTTON_ICON_PX[Size] || 24} {...LeadingIconProps} className={["show-leading-icon hidden-text", customIconClassName].filter(Boolean).join(" ")} />}
                    <span className="hidden-text">{children}</span>
                    {ShowTrailingIcon && TrailingIcon && <Icons name={TrailingIcon} size={TrailingIconProps.size || BUTTON_ICON_PX[Size] || 24} {...TrailingIconProps} className={["show-trailing-icon hidden-text", customIconClassName].filter(Boolean).join(" ")} />}
                    <div className="loader-container">
                        <div className={loading_color_class}>
                            <div className="loader-box loader_box1"></div>
                            <div className="loader-box loader_box2"></div>
                            <div className="loader-box loader_box3"></div>
                        </div>
                    </div>
                </>
            );
        }
        return (
            <>
                {ShowLeadingIcon && LeadingIcon && <Icons name={LeadingIcon} size={LeadingIconProps.size || BUTTON_ICON_PX[Size] || 24} {...LeadingIconProps} className={["show-leading-icon", customIconClassName].filter(Boolean).join(" ")} />}
                <span>{children}</span>
                {ShowTrailingIcon && TrailingIcon && <Icons name={TrailingIcon} size={TrailingIconProps.size || BUTTON_ICON_PX[Size] || 24} {...TrailingIconProps} className={["show-trailing-icon", customIconClassName].filter(Boolean).join(" ")} />}
            </>
        );
    };

    return (
        <div style={{ position: "relative", display: "flex", flexDirection: 'column', ...buttonStyle }}>
            <button
                type={type}
                className={computed_classes}
                style={innerButtonStyle}
                disabled={disabled}
                aria-busy={isLoading || undefined}
                onClick={isLoading ? undefined : onClick}
            >
                {renderButtonContent()}
            </button>
            <ConditionalRender if={error}>
                <div className="helper-text body-s">
                    {error}
                </div>
            </ConditionalRender>
        </div>
    );
};

// ─── Icon-only button sizes & types ─────────────────────────────────────

const ICON_SIZES = {
    ExtraSmall: "icon-extra-small",
    Small: "icon-small",
    Medium: "icon-medium",
    Large: "icon-large",
};
// Glyph size (px) inside the box. xs → 16, others → 24.
const ICON_GLYPH_PX = {
    ExtraSmall: 16,
    Small: 20,
    Medium: 24,
    Large: 24,
};
const ICON_TYPES = {
    Primary: "icon-primary-button",
    Secondary: "icon-secondary-button",
    Tertiary: "icon-tertiary-button",
    DangerTertiary: "icon-danger-tertiary-button",
    Danger: "icon-danger-button",
};

/**
 * IconButton — A button that only contains an icon (no text label).
 *
 * Use for compact actions like "edit", "delete", "copy" where the icon
 * is self-explanatory (pair with a Tooltip for accessibility).
 *
 * @param {string}  Size      - 'ExtraSmall' | 'Small' | 'Medium' | 'Large' (24 / 32 / 40 / 48 px box; xs uses a 16px glyph, others 24px)
 * @param {string}  IconName  - Icon name (from design_system/icons)
 * @param {string}  Type      - 'Primary' | 'Secondary' | 'Tertiary' | 'DangerTertiary' | 'Danger'
 * @param {func}    onClick   - Click handler
 * @param {boolean} disabled  - Disabled state
 * @param {string}  ariaLabel - Required for accessibility — describes the button action to screen readers
 * @param {string}  error     - Error message rendered below
 */
export const IconButton = ({
    Size = "Medium",
    IconName = null,
    IconProps = {},
    Type = "Primary",
    onClick,
    type = "button",
    disabled = false,
    ShowHover = true,
    IsSelected = false,
    ariaLabel = "",
    customClassName = "",
    buttonStyle = {},
    innerButtonStyle = {},
    error = "",
}) => {
    const computed_classes = useMemo(
        () => [
            ICON_TYPES[Type],
            ICON_SIZES[Size],
            !ShowHover ? 'icon-button--no-hover' : '',
            IsSelected ? 'icon-button--selected' : '',
            customClassName
        ].filter(Boolean).join(" "),
        [Type, Size, ShowHover, IsSelected, customClassName]
    );

    return (
        <div style={{ position: "relative", display: "inline-flex", flexDirection: "column", ...buttonStyle }}>
            <button
                type={type}
                className={computed_classes}
                style={innerButtonStyle}
                disabled={disabled}
                aria-label={ariaLabel || undefined}
                onClick={onClick}
            >
                {<Icons name={IconName} {...IconProps} size={ICON_GLYPH_PX[Size] || 24} />}
            </button>
            <ConditionalRender if={error}>
                <div className="support-s color-text-error mt-s">
                    {error}
                </div>
            </ConditionalRender>
        </div>
    );
};

/**
 * ButtonBlock — Full-width button (400px).
 * Convenience wrapper around Button for forms and modals.
 */
export const ButtonBlock = (props) => {
    return (
        <Button buttonStyle={{ width: 400 }} {...props} />
    );
};

/**
 * StickyButton — Floating action button.
 * White circular button with icon, subtle shadow, and teal border on hover.
 * Useful for persistent CTAs like guidelines that stay visible on scroll.
 *
 * @param {string} IconName  - DS icon name (kebab-case)
 * @param {func}   onClick   - Click handler
 * @param {string} ariaLabel - Accessible label
 * @param {object} buttonStyle - Additional styles on the outer wrapper
 */
export const StickyButton = ({ IconName, onClick, ariaLabel = "", buttonStyle = {} }) => {
    return (
        <div className="sticky-button" style={buttonStyle}>
            <button
                type="button"
                className="sticky-button__btn"
                aria-label={ariaLabel || undefined}
                onClick={onClick}
            >
                <Icons name={IconName} size={24} />
            </button>
        </div>
    );
};
